// Mixpanel tracking utilities
import mixpanel from "mixpanel-browser";

const MIXPANEL_TOKEN = "117a075cc3544152d743b711d917def3";

/**
 * Initialize Mixpanel
 * Call this once when the app starts (in index.tsx)
 */
export function initMixpanel() {
  if (!MIXPANEL_TOKEN) {
    console.warn("[Mixpanel] Token not configured");
    return;
  }

  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      api_host: "https://api-eu.mixpanel.com",
      debug: false,
      track_pageview: false,
      opt_out_tracking_by_default: true, // Locked until user type is verified
      autotrack: false,
      autocapture: false,
      persistence: "localStorage",
      ignore_dnt: true,
      record_sessions_percent: 100,
      record_mask_text_selector: "",
      record_block_selector: "",
      record_mask_text_class: "",
      record_block_class: "",
      record_collect_fonts: true,
      record_idle_timeout_ms: 300000,
      record_heatmap_data: true,
    });
  } catch (error) {
    console.error("[Mixpanel] Initialization failed:", error);
  }
}

/**
 * Identify user in Mixpanel after login
 * Internal users (Admin / CustomerSuccess / Sales) are NOT tracked
 */
export function identifyMixpanelUser(user: {
  userId: number;
  userName?: string;
  userEmail?: string;
  userType?: string;
  userRole?: string;
  actorName?: string;
  actorType?: string;
  clientId?: number;
  clientType?: string;
  accountType?: string;
  userAgeInDays?: number;
  actorAgeInDays?: number;
}) {
  if (!MIXPANEL_TOKEN) return;

  try {
    mixpanel.opt_in_tracking();

    mixpanel.identify(user.userId.toString());

    mixpanel.people.set({
      $name: user.userName || "N/A",
      $email: user.userEmail || "N/A",
      user_id: user.userId,
      user_role: user.userRole || "N/A",
      user_type: user.userType || "N/A",
      actor_name: user.actorName || "N/A",
      actor_type: user.actorType || "N/A",
      client_id: user.clientId ?? null,
      client_type: user.clientType || "N/A",
      account_type: user.accountType || "N/A",
      user_age_days: user.userAgeInDays ?? null,
      actor_age_days: user.actorAgeInDays ?? null,
      last_login: new Date().toISOString(),
    });

    mixpanel.start_session_recording();
  } catch (error) {
    console.error("[Mixpanel] User identification failed:", error);
  }
}

/**
 * Track a custom event
 */
export function trackMixpanelEvent(eventName: string, properties?: Record<string, unknown>) {
  if (!MIXPANEL_TOKEN) return;

  try {
    mixpanel.track(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    console.error("[Mixpanel] Event tracking failed:", error);
  }
}

/**
 * Track a page / route view
 */
export function trackMixpanelPageView(pageName: string, extraProps?: Record<string, unknown>) {
  if (!MIXPANEL_TOKEN) return;

  try {
    mixpanel.track("Page View", {
      page_name: pageName,
      page_url: window.location.pathname,
      ...extraProps,
    });
  } catch (error) {
    console.error("[Mixpanel] Page view tracking failed:", error);
  }
}

/**
 * Decode a JWT token payload without verification (client-side only).
 * Returns the parsed payload object, or null if the token is invalid.
 */
function decodeJWTPayload(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

/**
 * Identify a vendor portal user using email + PAN as unique identity.
 * All vendor portal users are external — they are always opted in.
 * Distinct ID is set to `<email>_<PAN>` (lowercase email, uppercase PAN).
 */
export function identifyVendorPortalUser(email: string, pan: string) {
  if (!MIXPANEL_TOKEN) return;
  if (!email && !pan) {
    console.warn("[Mixpanel] Vendor portal: no email or PAN available for identification");
    return;
  }

  try {
    mixpanel.opt_in_tracking();

    const distinctId = (email || "").toLowerCase();
    mixpanel.identify(distinctId);

    mixpanel.people.set({
      $email: email || "N/A",
      pan: (pan || "").toUpperCase() || "N/A",
    });

    mixpanel.start_session_recording();
  } catch (error) {
    console.error("[Mixpanel] Vendor portal identification failed:", error);
  }
}

/**
 * Extract email and PAN from a vendor portal JWT token payload.
 * Returns { email, pan } — either may be null if not found in the token.
 */
export function extractVendorPortalIdentityFromToken(token: string): {
  email: string | null;
  pan: string | null;
} {
  const payload = decodeJWTPayload(token);
  if (!payload) return { email: null, pan: null };

  // Try common claim names for email and pan
  const email = (payload.email as string) || (payload.emailId as string) || (payload.sub as string) || null;

  const pan = (payload.pan as string) || (payload.PAN as string) || (payload.vendorPan as string) || null;

  return { email, pan };
}

/**
 * Reset user identity on logout
 */
export function resetMixpanelUser() {
  if (!MIXPANEL_TOKEN) return;

  try {
    mixpanel.opt_out_tracking();
    mixpanel.reset();
  } catch (error) {
    console.error("[Mixpanel] Reset failed:", error);
  }
}

/**
 * Start timing an event (call trackMixpanelEvent with the same name to finish)
 */
export function startTimedEvent(eventName: string) {
  if (!MIXPANEL_TOKEN) return;

  try {
    mixpanel.time_event(eventName);
  } catch (error) {
    console.error("[Mixpanel] Time event start failed:", error);
  }
}

/**
 * Register super properties sent with every subsequent event
 */
export function setMixpanelSuperProperties(properties: Record<string, unknown>) {
  if (!MIXPANEL_TOKEN) return;

  try {
    mixpanel.register(properties);
  } catch (error) {
    console.error("[Mixpanel] Super properties failed:", error);
  }
}

// Export raw instance for advanced usage
export { mixpanel };
